import React, { useEffect, useState } from 'react';
import { orgAPI } from '@/services/api';
import { Briefcase, Filter, Search, AlertCircle, Crown } from 'lucide-react';

export default function RecruiterOrgJobs() {
  const [ctx, setCtx] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [emailFilter, setEmailFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await orgAPI.getContext();
      setCtx(c);
      if (c.is_head) {
        const r = await orgAPI.getOrgJobs({ recruiter_email: emailFilter, status: statusFilter, limit: 100 });
        setJobs(r.jobs || []);
        setTotal(r.total || 0);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load();}, []);

  const applyFilters = (e) => { e.preventDefault(); load(); };

  if (loading) return <div className="text-slate-500 dark:text-zinc-400 text-sm" data-testid="org-jobs-loading">Loading…</div>;
  if (!ctx?.is_head) {
    return (
      <div className="text-slate-500 dark:text-zinc-400 text-sm flex items-start gap-2 p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg" data-testid="org-jobs-not-head">
        <AlertCircle className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400" />
        <span>This page is visible to the recruiter head only.</span>
      </div>
    );
  }

  return (
    <div data-testid="recruiter-org-jobs">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Crown className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white tracking-tight">All organization jobs</h1>
          </div>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">{total} job{total !== 1 && 's'} across {ctx.organization?.name}</p>
        </div>
      </div>

      <form onSubmit={applyFilters} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="org-jobs-filters">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500 dark:text-zinc-500" />
          <input value={emailFilter} onChange={e => setEmailFilter(e.target.value)} placeholder="Filter by recruiter email…" className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600" data-testid="email-filter-input" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white" data-testid="status-filter-select">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="archived">Archived</option>
          <option value="closed">Closed</option>
        </select>
        <button type="submit" className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium" data-testid="apply-filter-btn"><Filter className="w-3.5 h-3.5" /> Apply</button>
      </form>

      {error && <div className="bg-red-500/10 text-red-600 dark:text-red-400 px-4 py-2 rounded-lg mb-4 text-sm" data-testid="org-jobs-error">{error}</div>}

      <div className="space-y-2">
        {jobs.length === 0 ? (
          <div className="text-slate-500 dark:text-zinc-500 text-sm py-12 text-center" data-testid="no-org-jobs">No jobs found for the selected filters.</div>
        ) : jobs.map(j => (
          <div key={j.id} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 flex items-center justify-between" data-testid={`org-job-${j.id}`}>
            <div className="flex items-start gap-3">
              <Briefcase className="w-4 h-4 text-slate-500 dark:text-zinc-500 mt-1" />
              <div>
                <p className="text-slate-900 dark:text-white font-medium">{j.title}</p>
                <p className="text-slate-500 dark:text-zinc-500 text-xs mt-0.5">Posted by <span className="text-slate-500 dark:text-zinc-400">{j.recruiter_email}</span> · {j.job_type || 'permanent'}{j.compensation_min ? ` · $${j.compensation_min.toLocaleString()}–$${(j.compensation_max || j.compensation_min).toLocaleString()}` : ''}</p>
              </div>
            </div>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${j.status === 'active' ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10' : 'text-slate-500 dark:text-zinc-400 bg-zinc-500/10'}`}>{j.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
